'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Chip from '@enlighten/design-system/dist/Chip';
import Grid from '@enlighten/design-system/dist/Grid';
import Typography from '@enlighten/design-system/dist/Typography';
import { AnimatePresence, motion, useInView, useScroll } from 'motion/react';
import Image from 'next/image';
import { Fragment, useEffect, useRef, useState } from 'react';
import ImageProcess1 from '../../assets/images/king/img-process-1.png';
import ImageProcess2 from '../../assets/images/king/img-process-2.png';
import ImageProcess3 from '../../assets/images/king/img-process-3.png';
import ImageProcess4 from '../../assets/images/king/img-process-4.png';
import SearchIcon from '../../assets/images/king/search-icon.png';
import BarnImage from '../../assets/images/king/tab-barn.png';
import BarnContentPcImage from '../../assets/images/king/tab-barn-img.png';
import BarnContentMobileImage from '../../assets/images/king/tab-barn-img-m.png';
import FactoryImage from '../../assets/images/king/tab-factory.png';
import FactoryContentPcImage from '../../assets/images/king/tab-factory-img.png';
import FactoryContentMobileImage from '../../assets/images/king/tab-factory-img-m.png';
import HouseImage from '../../assets/images/king/tab-house.png';
import HouseContentPcImage from '../../assets/images/king/tab-house-img.png';
import HouseContentMobileImage from '../../assets/images/king/tab-house-img-m.png';
import LandImage from '../../assets/images/king/tab-land.png';
import LandContentPcImage from '../../assets/images/king/tab-land-img.png';
import LandContentMobileImage from '../../assets/images/king/tab-land-img-m.png';
import { useSticky } from '../StickyProvider';
const tabs = [
    {
        label: '예비 사업주',
        title: '태양광 발전소가\n처음이신 분',
        value: 'expected',
    },
    {
        label: '사업주',
        title: '태양광 발전소를\n가지고 있는 분',
        value: 'owner',
    },
];
const subTabs = [
    {
        icon: FactoryImage,
        title: '건물/공장',
        value: 'factory',
        contentTitle: '건물/공장 지붕에\n태양광 설치 시 20년 동안',
        contentImage: {
            pc: FactoryContentPcImage,
            mobile: FactoryContentMobileImage,
            alt: '건물/공장 지붕에 태양광 설치 시 20년 동안 벌 수 있는 돈은 총 52억 6천만 원(3,300평 기준), 아낄 수 있는 돈은 총 28억 9천 7백만 원, 설치 후 월 전기료를 77% 절감',
        },
    },
    {
        icon: LandImage,
        title: '토지',
        value: 'land',
        contentTitle: '토지에\n태양광 설치 시 20년 동안',
        contentImage: {
            pc: LandContentPcImage,
            mobile: LandContentMobileImage,
            alt: '토지에 태양광 설치 시 20년 동안 총 4억 1천만원(300평 기준), 매월 평균 1백 7십만원',
        },
    },
    {
        icon: HouseImage,
        title: '주택',
        value: 'house',
        contentTitle: '주택 지붕에\n태양광 설치 시 20년 동안',
        contentImage: {
            pc: HouseContentPcImage,
            mobile: HouseContentMobileImage,
            alt: '주택 지붕에 태양광 설치 시 20년 동안 5평일 때 아낄 수 있는 돈은 총 5천 2백만원, 설치 후 월 1만 5천원(77%) 절감',
        },
    },
    {
        icon: BarnImage,
        title: '축사',
        value: 'barn',
        contentTitle: '축사 지붕에\n태양광 설치 시 20년 동안',
        contentImage: {
            pc: BarnContentPcImage,
            mobile: BarnContentMobileImage,
            alt: '축사 지붕에 태양광 설치 시 20년 동안 벌 수 있는 돈은 총 3억 1백만원(334평 기준), 아낄 수 있는 돈은 총 2억원, 설치 후 월 전기료를 82% 절감',
        },
    },
];
const flowSteps = [
    { label: '태양광 전문\n무료 상담', image: ImageProcess1 },
    { label: '수익성 분석을 통한\n딱 맞는 금융 상품 연결', image: ImageProcess2 },
    { label: '복잡한 서류 작업\n개발 행위 허가 대행', image: ImageProcess3 },
    { label: '믿고 맡길 수 있는\n완벽한 시공', image: ImageProcess4 },
];
export default function KingSticky({ top = 0 }) {
    const [tab, setTab] = useState('expected');
    const [subTab, setSubTab] = useState('factory');
    const expectedRef = useRef(null);
    const ownerRef = useRef(null);
    const textAnimatedTypeRef = useRef(null);
    const inViewTextAnimatedType = useInView(textAnimatedTypeRef);
    const { scrollY } = useScroll();
    const sticky = useSticky();
    useEffect(() => {
        return scrollY.on('change', (value) => {
            const SUB_TAB_SCROLL_POSITION = window.scrollY + (ownerRef.current?.getBoundingClientRect().top || 0);
            if (value >= SUB_TAB_SCROLL_POSITION && tab !== 'owner') {
                setTab('owner');
            }
            else if (value < SUB_TAB_SCROLL_POSITION && tab !== 'expected') {
                setTab('expected');
            }
        });
    }, [scrollY, tab]);
    return (_jsxs(_Fragment, { children: [_jsx(Grid, { position: sticky ? 'sticky' : 'initial', top: top, container: true, bgcolor: "common.white", borderTop: (theme) => `1px solid ${theme.palette.grey['300']}`, borderBottom: (theme) => `1px solid ${theme.palette.grey['300']}`, zIndex: 999, children: tabs.map(({ label, title, value }, i) => (_jsx(Button, { variant: "text", onClick: () => {
                        setTab(value);
                        switch (value) {
                            case 'expected':
                                expectedRef.current?.scrollIntoView({ behavior: 'smooth' });
                                break;
                            case 'owner':
                                ownerRef.current?.scrollIntoView({ behavior: 'smooth' });
                                break;
                        }
                    }, sx: {
                        flex: 1,
                        height: 'auto',
                        paddingX: 0,
                        paddingY: { xs: '12px', md: '27px' },
                        ':hover': {
                            bgcolor: 'grey.300',
                        },
                    }, children: _jsxs(Grid, { container: true, direction: { xs: 'column', md: 'row' }, justifyContent: "center", alignItems: "center", width: "100%", height: { xs: '100%', md: 44 }, borderLeft: (theme) => i > 0 ? `1px solid ${theme.palette.grey['300']}` : 'none', children: [_jsx(Chip, { label: label, sx: {
                                    bgcolor: tab === value ? 'primary.main' : 'grey.200',
                                    height: { xs: 25.5, md: 36 },
                                    borderRadius: 99,
                                    padding: { xs: '6px 10px', md: '10px 20px' },
                                    '.MuiChip-label': {
                                        color: tab === value ? 'common.white' : 'grey.400',
                                        fontSize: { xs: 9, md: 16 },
                                        fontWeight: 700,
                                        lineHeight: 1.23,
                                        padding: 0,
                                    },
                                } }), _jsx(Typography, { fontSize: { xs: 16, md: 22 }, fontWeight: 700, lineHeight: 1.5, whiteSpace: { xs: 'pre-line', md: 'normal' }, color: tab === value ? 'grey.900' : 'grey.400', margin: { xs: '10px 0 0 0', md: '0 0 0 17px' }, children: title })] }) }, i))) }), _jsx("div", { ref: expectedRef, children: _jsxs(Grid, { container: true, direction: "column", alignItems: "center", marginTop: { xs: '60px', md: '100px' }, paddingTop: { xs: '60px', md: '100px' }, children: [_jsx(Chip, { label: "\uC608\uBE44 \uC0AC\uC5C5\uC8FC", sx: {
                                bgcolor: 'primary.main',
                                borderRadius: 99,
                                padding: { xs: '12px', md: '20px' },
                                '.MuiChip-label': {
                                    fontSize: { xs: 12, md: 16 },
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: 'common.white',
                                    padding: 0,
                                },
                            } }), _jsx(Typography, { fontSize: { xs: 24, md: 40 }, fontWeight: 700, textAlign: "center", lineHeight: 1.5, whiteSpace: { xs: 'pre-line', md: 'normal' }, marginTop: "20px", marginBottom: { xs: 3, md: 5 }, children: '태양광 재테크로 얼마나\n벌 수 있는지 궁금하다면' })] }) }), _jsxs(Grid, { container: true, direction: "column", alignItems: "center", width: "100%", paddingBottom: { xs: '111px', md: 25 }, paddingX: 3, children: [_jsx("div", { ref: textAnimatedTypeRef, children: _jsx(AnimatePresence, { children: inViewTextAnimatedType && (_jsxs(Grid, { display: "flex", justifyContent: "space-between", alignItems: "center", border: "2px solid #0B74E2", borderRadius: 99, marginTop: "20px", padding: { xs: '13px 26px', md: '18px 36px' }, sx: {
                                    '& img': {
                                        width: { xs: 23, md: 30 },
                                        height: 'auto',
                                        marginLeft: { xs: 1, md: 2 },
                                    },
                                }, children: [_jsx(Typography, { fontSize: { xs: 16, md: 20 }, fontWeight: 700, whiteSpace: "nowrap", color: "#0B74E2", overflow: "hidden", marginX: "auto", sx: {
                                            animation: 'textTypeAnimated 1.5s steps(40, end)',
                                            '@keyframes textTypeAnimated': {
                                                from: { width: '0' },
                                                to: { width: '100%' },
                                            },
                                        }, children: "\uC8FC\uC18C \uC785\uB825\uB9CC \uD558\uBA74 \uC218\uC775\uB960 \uD655\uC778\uC774 \uB531!" }), process.env.NEXT_PUBLIC_IS_NEXT_JS === 'true' ||
                                        process.env.NEXT_PRIVATE_IS_NEXT_JS === 'true' ? (_jsx(Image, { src: SearchIcon, alt: "search" })) : (_jsx("img", { src: SearchIcon, alt: "search" }))] })) }) }), _jsx(Grid, { display: "flex", justifyContent: "space-between", width: "100%", maxWidth: 800, marginTop: "70px", children: subTabs.map(({ icon, title, value }, i) => (_jsxs(Button, { variant: "text", onClick: () => {
                                setSubTab(value);
                            }, sx: {
                                flexDirection: 'column',
                                width: { xs: 'auto', md: '20%' },
                                minWidth: 0,
                                height: 'auto',
                                padding: 0,
                                ':hover': {
                                    bgcolor: 'transparent',
                                },
                                '& img': {
                                    width: '100%',
                                    minWidth: 0,
                                    maxWidth: { xs: 60, md: 90 },
                                    height: 'auto',
                                },
                            }, children: [process.env.NEXT_PUBLIC_IS_NEXT_JS === 'true' ||
                                    process.env.NEXT_PRIVATE_IS_NEXT_JS === 'true' ? (_jsx(Image, { src: icon, alt: "", style: {
                                        filter: subTab === value
                                            ? 'none'
                                            : 'opacity(50%) grayscale(100%)',
                                        marginBottom: '10px',
                                        transition: 'filter 0.3s ease',
                                    } })) : (_jsx("img", { src: icon, alt: "", style: {
                                        filter: subTab === value
                                            ? 'none'
                                            : 'opacity(50%) grayscale(100%)',
                                        marginBottom: '10px',
                                        transition: 'filter 0.3s ease',
                                    } })), _jsx(Grid, { display: "flex", justifyContent: "center", alignItems: "center", bgcolor: subTab === value ? 'common.black' : 'grey.200', width: { xs: 65, md: 100 }, height: { xs: 34, md: 44 }, borderRadius: { xs: '6px', md: '10px' }, sx: { transition: 'all 0.3s ease' }, children: _jsx(Typography, { fontSize: { xs: 14, md: 16 }, fontWeight: 700, color: subTab === value ? 'common.white' : 'common.black', children: title }) })] }, i))) }), subTabs.map(({ value, contentTitle, contentImage }, i) => subTab === value ? (_jsxs(Grid, { container: true, direction: "column", alignItems: "center", width: "100%", maxWidth: 1400, border: (theme) => `1px solid ${theme.palette.grey['300']}`, borderRadius: { xs: 4, md: 10 }, marginTop: "50px", paddingTop: { xs: '30px', md: 0 }, paddingX: 3, overflow: "hidden", boxShadow: "0 4px 10px #0000000D", sx: {
                            '& img': {
                                width: '100%',
                                height: 'auto',
                            },
                        }, children: [_jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.8 }, children: _jsx(Typography, { fontSize: { xs: 20, md: 28 }, fontWeight: 700, textAlign: "center", lineHeight: 1.3, marginY: { xs: '20px', md: '28px' }, children: contentTitle }) }), _jsxs(motion.div, { initial: { opacity: 0, y: 100 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -100 }, transition: { duration: 0.3 }, children: [_jsx(Box, { display: { xs: 'none', md: 'block' }, marginTop: "45px", children: process.env.NEXT_PUBLIC_IS_NEXT_JS === 'true' ||
                                            process.env.NEXT_PRIVATE_IS_NEXT_JS === 'true' ? (_jsx(Image, { src: contentImage.pc, alt: contentImage.alt })) : (_jsx("img", { src: contentImage.pc, alt: contentImage.alt })) }), _jsx(Box, { display: { xs: 'block', md: 'none' }, marginTop: "20px", children: process.env.NEXT_PUBLIC_IS_NEXT_JS === 'true' ||
                                            process.env.NEXT_PRIVATE_IS_NEXT_JS === 'true' ? (_jsx(Image, { src: contentImage.mobile, alt: contentImage.alt })) : (_jsx("img", { src: contentImage.mobile, alt: contentImage.alt })) })] })] }, i)) : (_jsx(Fragment, {}, i)))] }), _jsxs(Grid, { container: true, direction: "column", alignItems: "center", width: "100%", paddingX: 3, paddingBottom: { xs: '60px', md: 25 }, children: [_jsx(Typography, { fontSize: { xs: 24, md: 40 }, fontWeight: 700, lineHeight: 1.5, textAlign: "center", whiteSpace: { xs: 'pre-line', md: 'normal' }, marginBottom: { xs: '10px', md: '20px' }, children: '상담부터 건설까지 발전왕에서\n모두 해드리니까,' }), _jsx(Typography, { fontSize: { xs: 18, md: 30 }, textAlign: "center", whiteSpace: { xs: 'pre-line', md: 'normal' }, children: '사업주님은 놀던 땅/건물/지붕에서\n돈만 벌어가세요.' }), _jsx(Box, { display: "flex", alignItems: "center", justifyContent: "center", width: "100%", maxWidth: 1394, marginTop: { xs: '40px', md: 7 }, flexDirection: { xs: 'column', md: 'row' }, sx: {
                            containerType: 'inline-size',
                        }, children: flowSteps.map((step, i) => (_jsxs(Fragment, { children: [i > 0 && (_jsx(Box, { sx: {
                                        width: { xs: '1px', md: '100%' },
                                        height: { xs: 40, md: '1px' },
                                        bgcolor: 'grey.300',
                                    } })), _jsxs(Box, { sx: {
                                        width: '280px',
                                        height: '280px',
                                        borderRadius: '50%',
                                        border: '1px solid #EBEDEF',
                                        boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.05)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        containerType: 'inline-size',
                                        '@container (max-width: 1200px)': {
                                            width: '200px',
                                            height: '200px',
                                        },
                                    }, children: [_jsx(Box, { component: Image, src: step.image, alt: "", sx: {
                                                width: { xs: 70, md: 90 },
                                                height: { xs: 70, md: 90 },
                                            } }), _jsx(Typography, { fontSize: "18px", lineHeight: { xs: '16px', md: '22px' }, textAlign: "center", whiteSpace: "pre-line", color: "#1D1E1F", sx: {
                                                '@container (max-width: 200px)': {
                                                    fontSize: 14,
                                                },
                                            }, children: step.label })] })] }, i))) }), _jsx("div", { ref: ownerRef, children: _jsxs(Grid, { container: true, direction: "column", alignItems: "center", paddingTop: { xs: '150px', md: 25 }, children: [_jsx(Chip, { label: "\uC0AC\uC5C5\uC8FC", sx: {
                                        bgcolor: 'primary.main',
                                        borderRadius: 99,
                                        padding: { xs: '12px', md: '20px' },
                                        marginBottom: '20px',
                                        '.MuiChip-label': {
                                            fontSize: { xs: 12, md: 16 },
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            color: 'common.white',
                                            padding: 0,
                                        },
                                    } }), _jsx(Typography, { fontSize: { xs: 24, md: 40 }, fontWeight: 700, textAlign: "center", lineHeight: 1.5, whiteSpace: { xs: 'pre-line', md: 'normal' }, marginBottom: { xs: '10px', md: '20px' }, children: '귀찮은 발전소 관리,\n무료로 받고 싶다면' }), _jsx(Typography, { display: { xs: 'none', md: 'block' }, fontSize: 30, textAlign: "center", children: "\uBC1C\uC804\uC655\uC5D0 \uBC1C\uC804\uC18C \uB4F1\uB85D\uB9CC \uD558\uBA74 \uB204\uAD6C\uB098 \uC790\uB3D9 \uAD00\uB9AC\uB97C 24\uC2DC\uAC04 365\uC77C!" })] }) })] })] }));
}
